import React, { useEffect, useState } from 'react'
import { Form, Field } from 'react-final-form'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import { Button, Typography, Grid, Paper, CircularProgress } from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { useSnackbar } from 'notistack'
import InputText from '../../components/Form/inputFullPage/InputText'
import InputTextArea from '../../components/Form/inputFullPage/InputTextArea'
import InputSelect, { InputSelectOnChange } from '../../components/Form/inputFullPage/InputSelect'
import SelectMenuItem from '../../components/Form/inputFullPage/SelectMenuItem'
import InputCheckbox from '../../components/Form/inputFullPage/InputCheckbox'
import { getConvocationDetails, saveConvocationDetails } from '../../containers/DataContainer'
import { dateDisplay } from '../../utils/Date'
import {
	PLATFORM_ID_COOKIE
} from '../../constants/cookies'

const useStyles = makeStyles((theme) => ({
	root: {
		padding: 24,
	},
	paper: {
		padding: 24,
		marginBottom: 24,
	},
	sectionTitle: {
		marginBottom: 4,
		fontWeight: 'bold',
		color: theme.palette.secondary.main,
	},
	buttonContainer: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: 24,
	},
	submitButton: {
		backgroundColor: `${theme.palette.secondary.main} !important`, // Même bleu que le texte des inputs
		color: 'white !important', // Texte blanc forcé
		'&:hover': {
			backgroundColor: `${theme.palette.secondary.dark} !important`, // Bleu plus foncé au survol
		},
	},
	rootText: {
		color: theme.palette.secondary.main,
		fontFamily: 'Helvetica',
		fontSize: '1.2em',
		fontWeight: 500,
	},
	rootInput: {
		width: '100%',
		marginTop: '25px',
	},
	sectionSpacing: {
		marginTop: '40px',
	},
	gridItemSpacingLeft: {
		paddingLeft: '16px !important',
	},
	loaderContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px',
	},
	errorMessage: {
		backgroundColor: theme.palette.error?.main || '#f44336',
		color: 'white',
		padding: '12px 24px',
		borderRadius: '4px',
		marginBottom: '16px',
		textAlign: 'center',
	},
	pageTitle: {
		color: theme.palette.secondary.main,
		fontWeight: 500,
		fontSize: '2.2em',
		marginBottom: '24px',
	},
}))

function Convocation() {
	const classes = useStyles()
	const { t } = useTranslation(['user'])
	const { enqueueSnackbar } = useSnackbar()
	const history = useHistory()
	const { pdpId, rpdNumero, entId } = useParams()
	const [cookies] = useCookies()
	const [convocationData, setConvocationData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState(null)
	const [isSaving, setIsSaving] = useState(false)

	// Fonction pour récupérer les données de la convocation
	const fetchConvocationData = () => {
		setIsLoading(true)
		setError(null)

		// Utiliser les paramètres corrects : pdpId, rpdumero, entId
		getConvocationDetails(cookies[PLATFORM_ID_COOKIE], pdpId, rpdNumero, entId, (res, err) => {
			setIsLoading(false)
			if (err) {
				console.error('Error fetching convocation details:', err)
				setError(err)
				return
			}
			if (res && res.data) {
				// Vérifier si les représentants sont identiques pour définir l'état de la checkbox
				const sameRepresentant = res.data.representantInspectionCommune === res.data.representantReunionCoordination
				
				// Ajouter la propriété separerNomRepresentant aux données et formater les dates
				const dataWithCheckbox = {
					...res.data,
					// La checkbox est cochée uniquement si l'inspection est acceptée ET que les représentants sont identiques
					separerNomRepresentant: res.data.confirmationInspectionCommune === true && sameRepresentant,
					// Formater les dates en dd/MM/yyyy HH:mm
					dateInspectionPrealable: res.data.dateInspectionPrealable ? dateDisplay(res.data.dateInspectionPrealable) : '',
					dateReunionCoordination: res.data.dateReunionCoordination ? dateDisplay(res.data.dateReunionCoordination) : ''
				}
				
				setConvocationData(dataWithCheckbox)
			} else {
				console.warn('No data in API response:', res)
				setError('Aucune donnée reçue de l\'API')
			}
		})
	}

	// useEffect pour charger les données au montage du composant
	useEffect(() => {
		if (pdpId && rpdNumero && entId) {
			fetchConvocationData()
		} else {
			console.error('Missing parameters:', { pdpId, rpdNumero, entId })
			setIsLoading(false)
			setError('Paramètres manquants dans l\'URL')
		}
	}, [pdpId, rpdNumero, entId])

	// Fonction de validation
	const validate = (values) => {
		const errors = {}
		
		if (!values.confirmationInspectionCommune && values.confirmationInspectionCommune !== false) {
			errors.confirmationInspectionCommune = 'Ce champ est obligatoire'
		}
		
		if (!values.confirmationReunionCoordination && values.confirmationReunionCoordination !== false) {
			errors.confirmationReunionCoordination = 'Ce champ est obligatoire'
		}
		
		// Représentant inspection commune obligatoire seulement si confirmation acceptée
		if (values.confirmationInspectionCommune === true && (!values.representantInspectionCommune || values.representantInspectionCommune.trim() === '')) {
			errors.representantInspectionCommune = 'Ce champ est obligatoire'
		}
		
		// Représentant réunion coordination obligatoire seulement si confirmation acceptée
		if (values.confirmationReunionCoordination === true && (!values.representantReunionCoordination || values.representantReunionCoordination.trim() === '')) {
			errors.representantReunionCoordination = 'Ce champ est obligatoire'
		}
		
		return errors
	}

	const onSubmit = (values) => {
		setIsSaving(true)
		setError(null)

		// Extraire uniquement les champs modifiables
		const modifiableData = {
			confirmationInspectionCommune: values.confirmationInspectionCommune,
			confirmationReunionCoordination: values.confirmationReunionCoordination,
			representantInspectionCommune: (values.representantInspectionCommune && values.representantInspectionCommune.trim()) || '',
			representantReunionCoordination: (values.representantReunionCoordination && values.representantReunionCoordination.trim()) || ''
		}

		// Envoyer les données à l'API
		saveConvocationDetails(cookies[PLATFORM_ID_COOKIE], pdpId, rpdNumero, entId, modifiableData, (res, err) => {
			setIsSaving(false)
			if (err) {
				console.error('Error saving convocation details:', err)
				setError('Erreur lors de l\'enregistrement des données')
				return
			}
			if (res) {
				// Show success message using snackbar like in other validation pages
				enqueueSnackbar(t('ConvocationSavedSuccessfully'), { variant: 'success' })
			}
		})
	}

	// Affichage du loader pendant le chargement
	if (isLoading) {
		return (
			<div className={classes.root}>
				<Typography variant="h4" className={classes.pageTitle}>
					{t('ConvocationDetails')}
				</Typography>
				<div className={classes.loaderContainer}>
					<CircularProgress />
				</div>
			</div>
		)
	}

	// Affichage d'une erreur si pas de données
	if (error || !convocationData) {
		return (
			<div className={classes.root}>
				<Typography variant="h4" className={classes.pageTitle}>
					{t('ConvocationDetails')}
				</Typography>
				<Paper className={classes.paper}>
					<Typography variant="h6" color="error">
						{error ? 'Erreur lors du chargement des données' : 'Aucune donnée disponible'}
					</Typography>
					<Typography variant="body2" style={{ marginTop: 16 }}>
						{error && typeof error === 'string' ? error : 'Veuillez vérifier les paramètres de l\'URL ou réessayer plus tard.'}
					</Typography>
				</Paper>
			</div>
		)
	}

	return (
		<div className={classes.root}>
			<Typography variant="h4" className={classes.pageTitle}>
				{t('ConvocationDetails')}
			</Typography>
			
			<Form
				onSubmit={onSubmit}
				validate={validate}
				initialValues={convocationData}
				render={({ handleSubmit, form, submitting, pristine, values }) => (
					<form onSubmit={handleSubmit}>
						{/* Section unique - Toutes les informations */}
						<Paper className={classes.paper}>
							{/* Informations PDP */}
							<Typography variant="h6" className={classes.sectionTitle}>
								{t('InformationPDP')}
							</Typography>
							<Grid container spacing={5}>
								<Grid item xs={12} sm={6}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="numeroPdP"
											label={t('pdpNumero')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="principalName"
											label= {t('principal')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="libellePdP"
											label={t('pdp_description')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="descriptionLieuxPdP"
											label={t('works_locations')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
							</Grid>

							{/* Dates et lieux */}
							<Grid container spacing={3}>
								<Grid item xs={12} sm={3}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="dateInspectionPrealable"
											label={t('dateInspectionPrealable')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={9} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="lieuInspectionPrealable"
											label={t('lieuInspectionPrealable')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={3}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="dateReunionCoordination"
											label={t('dateReunionCoordination')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={9} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="lieuReunionCoordination"
											label={t('lieuReunionCoordination')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
							</Grid>

							{/* Entreprise Extérieure */}
							<Typography variant="h6" className={`${classes.sectionTitle} ${classes.sectionSpacing}`}>
								{t('InformationsEntreprise')}
							</Typography>
							<Grid container spacing={5}>
								<Grid item xs={12} sm={6}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="entNom"
											label={t('companyName')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="entNumeroSiret"
											label={t('siretNumber')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="entRaisonSociale"
											label={t('socialReason')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<InputText
											name="entNomAgence"
											label={t('agencyName')}
											classes={{ root: classes.rootInput }}
											readOnly
										/>
									</Typography>
								</Grid>
							</Grid>

							{/* Nouveaux champs modifiables */}
							<Typography variant="h6" className={`${classes.sectionTitle} ${classes.sectionSpacing}`}>
								{t('ResponseToInvitations')}
							</Typography>
							<Grid container spacing={3}>
								{/* Confirmation présence Inspection commune préalable */}
								<Grid item xs={12} sm={6}>
									<Typography classes={{ root: classes.rootText }}>
										<Field name="confirmationInspectionCommune">
											{({ input, meta }) => (
												<InputSelectOnChange
													{...input}
													label={t('ConfirmationInspectionCommune') + " *"} 
													classes={{ root: classes.rootInput }}
													error={meta.error && meta.touched}
													helperText={meta.error && meta.touched && meta.error}
													onChange={(e) => {
														input.onChange(e)  // Mettre à jour le state du formulaire
														// Si confirmation refusée, décocher la checkbox et vider les champs
														if (e.target.value === false) {
															form.change('separerNomRepresentant', false)
															form.change('representantInspectionCommune', '')
														}
													}}
												>
													<SelectMenuItem value={true}>Acceptée</SelectMenuItem>
													<SelectMenuItem value={false}>Refusée</SelectMenuItem>
												</InputSelectOnChange>
											)}
										</Field>
									</Typography>
								</Grid>
								
								{/* Nom représentant Inspection */}
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<Field name="representantInspectionCommune">
											{({ input, meta }) => (
												<InputText
													{...input}
													label={`${t('NomRepresentantInspection')} ${values.confirmationInspectionCommune === true ? '*' : ''}`}
													classes={{ root: classes.rootInput }}
													error={meta.error && meta.touched}
													helperText={meta.error && meta.touched && meta.error}
													disabled={values.confirmationInspectionCommune !== true}
													onChange={(e) => {
														input.onChange(e)
														// Synchroniser avec le champ réunion si la checkbox est cochée
														if (values.separerNomRepresentant) {
															form.change('representantReunionCoordination', e.target.value)
														}
													}}
												/>
											)}
										</Field>
									</Typography>
								</Grid>
								
								{/* Confirmation présence Réunion de coordination */}
								<Grid item xs={12} sm={6}>
									<Typography classes={{ root: classes.rootText }}>
										<Field name="confirmationReunionCoordination">
											{({ input, meta }) => (
												<InputSelectOnChange
													{...input}
													label={t('ConfirmationReunionCoordination') + " *"}
													classes={{ root: classes.rootInput }}
													error={meta.error && meta.touched}
													helperText={meta.error && meta.touched && meta.error}
													onChange={(e) => {
														input.onChange(e)  // Mettre à jour le state du formulaire
														// Si confirmation refusée, vider le champ représentant réunion
														if (e.target.value === false) {
															form.change('representantReunionCoordination', '')
														}
													}}
												>
													<SelectMenuItem value={true}>Acceptée</SelectMenuItem>
													<SelectMenuItem value={false}>Refusée</SelectMenuItem>
												</InputSelectOnChange>
											)}
										</Field>
									</Typography>
								</Grid>
								
								{/* Nom représentant Réunion */}
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Typography classes={{ root: classes.rootText }}>
										<Field name="representantReunionCoordination">
											{({ input, meta }) => (
												<InputText
													{...input}
													label={`${t('NomRepresentantReunion')} ${values.confirmationReunionCoordination === true ? '*' : ''}`}
													classes={{ root: classes.rootInput }}
													error={meta.error && meta.touched}
													helperText={meta.error && meta.touched && meta.error}
													disabled={values.confirmationReunionCoordination !== true || values.separerNomRepresentant === true}
												/>
											)}
										</Field>
									</Typography>
								</Grid>
								
								{/* Espace vide pour aligner la checkbox sur la ligne 3 */}
								<Grid item xs={12} sm={6}>
									{/* Espace intentionnellement vide */}
								</Grid>
								
								{/* Checkbox pour séparer les noms - ligne 3, colonne droite */}
								<Grid item xs={12} sm={6} className={classes.gridItemSpacingLeft}>
									<Field name="separerNomRepresentant">
										{({ input, meta }) => (
											<InputCheckbox
												{...input}
												label={t('AvoirLeMemeRepresentant')}
												readOnly={values.confirmationInspectionCommune !== true}
												onChange={(e) => {
													const isChecked = e.target.checked
													input.onChange(isChecked)
												
													if (isChecked) {
														// Logique de copie intelligente
														if (values.representantInspectionCommune && !values.representantReunionCoordination) {
															// Si inspection rempli et réunion vide → copier inspection vers réunion
															form.change('representantReunionCoordination', values.representantInspectionCommune)
														} else if (!values.representantInspectionCommune && values.representantReunionCoordination) {
															// Si inspection vide et réunion remplie → copier réunion vers inspection
															form.change('representantInspectionCommune', values.representantReunionCoordination)
														} else if (values.representantInspectionCommune && values.representantReunionCoordination) {
															// Si les deux sont remplis → priorité à inspection (copier inspection vers réunion)
															form.change('representantReunionCoordination', values.representantInspectionCommune)
														}
														// Si les deux sont vides, on ne fait rien
													} else {
														// Vider le champ réunion pour permettre une saisie indépendante
														form.change('representantReunionCoordination', '')
													}
												}}
											/>
										)}
									</Field>
								</Grid>
							</Grid>
						</Paper>

						{/* Messages d'erreur */}
						{error && typeof error === 'string' && (
							<div className={classes.errorMessage}>
								<Typography variant="body1">
									{error}
								</Typography>
							</div>
						)}

						{/* Bouton Enregistrer */}
						<div className={classes.buttonContainer}>
							<Button
								type="submit"
								variant="contained"
								className={classes.submitButton}
								disabled={submitting || isSaving}
							>
								{isSaving ? t('Saving') : t('Save')}
							</Button>
						</div>
					</form>
				)}
			/>
		</div>
	)
}

export default Convocation
